/**
 * objPolypaint.js — read ZBrush polypaint out of an OBJ.
 *
 * WHY THIS EXISTS
 *
 * The team paints their case-report colours in ZBrush and exports OBJ. Those
 * colours travel in the file as `#MRGB` comment lines — and because they are
 * comments, every OBJ loader ignores them. So the design portal has been
 * discarding the exact colours the team chose and asking an operator to
 * re-pick them by hand.
 *
 * They are not approximations. Decoding the polypaint from a real July-2026
 * case report export gives byte-exact matches for the team's official palette:
 *
 *   Page 9 "Post plan skull"   #91DD6A 20.9%   (Mandible Osteotomy)
 *                              #D684E1  0.6%   (Genioplasty)
 *                              #FFFFFF 78.0%   (unpainted)
 *   Page 3 "Native skull"      #FFFFFF  100%   (entirely unpainted)
 *
 * Which also settles the colour semantics independently: the team's own native
 * page carries no paint, and colour appears only on segments the plan has moved.
 *
 * FORMAT
 *
 * `#MRGB ` is followed by a run of 8-hex-digit groups, one per vertex, in
 * vertex order: MM RR GG BB where MM is the mask channel and RRGGBB the colour.
 * ZBrush packs 64 per line and emits the whole block AFTER the `v` lines.
 *
 * WHY A SEPARATE PARSER
 *
 * three's OBJLoader de-indexes, so a colour array in original vertex order
 * cannot simply be attached afterwards — it would have to be re-expanded in
 * exactly OBJLoader's output order, including its per-group mesh splitting.
 * Rewriting the OBJ text to 6-component `v` lines works but doubles the peak
 * memory of a 119 MB export.
 *
 * So: a focused parser for exactly what ZBrush emits. It is used ONLY when
 * `#MRGB` is present; every other file still goes through OBJLoader untouched,
 * and tools/render-hash proves those paths do not move.
 */

/** Cheap check: is it worth running the polypaint parser at all? */
export const hasPolypaint = (text) => text.includes('#MRGB')

/**
 * sRGB byte → linear float.
 *
 * three treats a `color` attribute as LINEAR. The hex values are sRGB, and
 * feeding one to the other silently desaturates — the same trap already
 * documented for the label plate, where sRGB(0.504)/sRGB(0.636) = 0.908
 * accounted exactly for a colour that looked washed out.
 */
const SRGB_TO_LINEAR = (() => {
  const t = new Float32Array(256)
  for (let i = 0; i < 256; i++) {
    const c = i / 255
    t[i] = c < 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return t
})()

/**
 * Parse a ZBrush OBJ into one geometry payload per polygroup.
 *
 * @param {string} text
 * @returns {{groups: Array<{name:string, position:Float32Array, color:Float32Array|null}>,
 *            paintedVertices:number, palette:Map<string,number>}}
 */
export function parseZBrushObj(text) {
  const positions = []          // flat x,y,z in original vertex order
  const colors = []             // flat linear r,g,b in original vertex order
  /** @type {Map<string, number[]>} group name → flat list of 0-based vertex indices */
  const faceGroups = new Map()
  let current = 'default'
  faceGroups.set(current, [])

  let paintedVertices = 0
  const palette = new Map()     // '#RRGGBB' → count, for diagnostics and tests

  // Split once. The alternative — a regex sweep per line kind — is slower on the
  // 100 MB+ exports these actually are.
  const lines = text.split('\n')
  for (let li = 0; li < lines.length; li++) {
    const line = lines[li]
    if (!line) continue
    const c0 = line.charCodeAt(0)

    // 'v' (118) — but not 'vt'/'vn', which ZBrush does not emit here anyway
    if (c0 === 118 && (line.charCodeAt(1) === 32 || line.charCodeAt(1) === 9)) {
      const p = line.split(/\s+/)
      positions.push(+p[1], +p[2], +p[3])
      // A 6-component `v` line is the OTHER way OBJ carries vertex colour; if a
      // file uses it, take it and skip the #MRGB path for that vertex.
      if (p.length >= 7) {
        colors.push(+p[4], +p[5], +p[6])
      }
      continue
    }

    // 'f' (102)
    if (c0 === 102 && line.charCodeAt(1) === 32) {
      const p = line.split(/\s+/)
      const n = p.length - 1
      if (n < 3) continue
      const idx = new Array(n)
      for (let i = 0; i < n; i++) {
        // "12", "12/3", "12//4", "12/3/4", and negative (relative) indices
        const slash = p[i + 1].indexOf('/')
        const raw = +(slash === -1 ? p[i + 1] : p[i + 1].slice(0, slash))
        idx[i] = raw > 0 ? raw - 1 : positions.length / 3 + raw
      }
      const out = faceGroups.get(current)
      // Fan-triangulate, the same way OBJLoader does, so a quad splits identically.
      for (let i = 1; i < n - 1; i++) out.push(idx[0], idx[i], idx[i + 1])
      continue
    }

    // 'g' / 'o' (103 / 111)
    if ((c0 === 103 || c0 === 111) && line.charCodeAt(1) === 32) {
      current = line.slice(2).trim() || 'default'
      if (!faceGroups.has(current)) faceGroups.set(current, [])
      continue
    }

    // '#MRGB ' (35)
    if (c0 === 35 && line.startsWith('#MRGB ')) {
      const payload = line.slice(6).trim()
      for (let i = 0; i + 8 <= payload.length; i += 8) {
        // skip the mask byte; take RRGGBB
        const r = parseInt(payload.slice(i + 2, i + 4), 16)
        const g = parseInt(payload.slice(i + 4, i + 6), 16)
        const b = parseInt(payload.slice(i + 6, i + 8), 16)
        if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) continue
        colors.push(SRGB_TO_LINEAR[r], SRGB_TO_LINEAR[g], SRGB_TO_LINEAR[b])
        paintedVertices++
        const hex = `#${((r << 16) | (g << 8) | b).toString(16).toUpperCase().padStart(6, '0')}`
        palette.set(hex, (palette.get(hex) ?? 0) + 1)
      }
    }
  }

  const vertexCount = positions.length / 3
  // ZBrush pads the final #MRGB line to 64 entries, so the colour block can
  // overrun the vertex count by a few. Trim rather than mis-align everything.
  const haveColors = colors.length / 3 >= vertexCount && vertexCount > 0

  const groups = []
  for (const [name, indices] of faceGroups) {
    if (!indices.length) continue
    const n = indices.length
    const position = new Float32Array(n * 3)
    const color = haveColors ? new Float32Array(n * 3) : null
    for (let i = 0; i < n; i++) {
      const v = indices[i]
      position[i * 3] = positions[v * 3]
      position[i * 3 + 1] = positions[v * 3 + 1]
      position[i * 3 + 2] = positions[v * 3 + 2]
      if (color) {
        color[i * 3] = colors[v * 3]
        color[i * 3 + 1] = colors[v * 3 + 1]
        color[i * 3 + 2] = colors[v * 3 + 2]
      }
    }
    groups.push({ name, position, color })
  }

  return { groups, paintedVertices: haveColors ? paintedVertices : 0, palette }
}

/**
 * True when a parsed file carries paint that actually says something.
 *
 * A fully white polypaint is the ZBrush default for "not painted". Treating it
 * as a colour would be harmless with a matcap (white multiplies to a no-op) but
 * would wrongly report the file as coloured in the UI, and would suppress the
 * classifier fallback that files without paint rely on.
 */
export function hasMeaningfulPaint(palette, paintedVertices) {
  if (!paintedVertices) return false
  let nonWhite = 0
  for (const [hex, n] of palette) {
    if (hex !== '#FFFFFF' && hex !== '#FEFEFE') nonWhite += n
  }
  return nonWhite / paintedVertices > 0.001
}
